/// <amd-module name="Dashboards/DashboardView/src/components/body/VirtualizedGrid" />
import * as React from "react";

import { DashboardViewAwareState } from "../../model/DashboardViewState";
import { PresentationMode } from "../../model/PresentationMode";
import { BoundingRectangle, Viewport } from "../../model/Viewport";
import { Widget } from "../../model/Widget";
import { WidgetMap } from "../../model/WidgetMap";
import { DashboardActions } from "../../redux/actions/Actions";
import * as Selectors from "../../redux/selectors/Selectors";
import { connectWithPageContext } from "../../utilities/ConnectWithPageContext";
import { clampRectangleInWindow, scrollElementIntoViewport } from "../../utilities/Viewport";

import { DashboardGrid, GridMode } from "Dashboards/Grid/DashboardGrid";
import { GridItem } from "Dashboards/Grid/DashboardGridItem";
import { WidgetSizeConverter } from "Dashboards/Grid/WidgetSizeConverter";
import { WidgetHostProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetHost } from "Dashboards/WidgetHost/WidgetHost";

import { Dialog } from "VSSUI/Dialog";
import { ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { ITeamData } from "Tfs/Platform/Page";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { ITTIAwareState } from "VSS/Redux/Modules/TTIMonitor";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import * as Resources from "Dashboards/DashboardView/Resources";

interface VirtualizedGridOwnProps {
    renderOnEmpty: () => JSX.Element | null;
}

interface VirtualizedGridMappedProps {
    widgets: ReadonlyArray<Widget>;
    canEditDashboard: boolean;
    previewWidgetId: string | undefined;
    highlightOnPreview: boolean;

    /**
     * An associative array of widget instance ID key to boolean value.
     * A value of true means the widget/widget host should be rendered.
     * A value of false means a lightweight proxy will be rendered instead of the full widget/widget host.
     */
    shouldRenderWidgetMap: WidgetMap<boolean>;
    presentationMode: PresentationMode;
    widgetsShouldOpenLinksInNewTab: boolean;
    draggedWidgetId: string | undefined;
    draggedEvent: Event | undefined;
    team: ITeamData;
}

interface VirtualizedGridProps extends VirtualizedGridOwnProps, VirtualizedGridMappedProps { }

/**
 * Renders a dashboard grid that does not render grid item contents until they have been visible within the viewport at least once.
 */
class VirtualizedGrid extends React.Component<VirtualizedGridProps & typeof Actions> {
    private get viewport(): Viewport {
        const gridRectangle = this.gridRef.current!.getBoundingClientRect();
        return clampRectangleInWindow(gridRectangle);
    }

    private get gridItemBoundingRectangles(): WidgetMap<BoundingRectangle> {
        const boundingRects: WidgetMap<BoundingRectangle> = {};

        for (const widget of this.props.widgets) {
            const gridItemRefElement = this.gridItemRefElements[widget.id!];
            if (gridItemRefElement != null) {
                boundingRects[widget.id!] = gridItemRefElement.getBoundingClientRect();
            }
        }

        return boundingRects;
    }
    // Used to reduce the number of actions created by scroll events to help smooth scrolling
    private static readonly ThrottleOnViewportChangedMS = 300;

    // Strict limit on the number of columns in the grid.  
    private static readonly MaximumGridColumns = 32;

    private gridRef: React.RefObject<HTMLDivElement>;
    private gridItemRefElements: WidgetMap<HTMLDivElement | null>;
    private _previousPreviewWidgetId: string | undefined;
    private _onDeleteWidgetId = new ObservableValue<string | undefined>(undefined);

    private onViewportChanged = new TimerManagement().throttle((): void => {
        this.props.onViewportChanged(this.viewport, this.gridItemBoundingRectangles);
    }, VirtualizedGrid.ThrottleOnViewportChangedMS);

    public constructor(props: VirtualizedGridProps & typeof Actions) {
        super(props);

        this.gridRef = React.createRef();
        this.gridItemRefElements = {};
    }

    public componentDidMount(): void {
        this.gridRef.current!.addEventListener("scroll", this.onViewportChanged);
        window.addEventListener("scroll", this.onViewportChanged);
        window.addEventListener("resize", this.onViewportChanged);

        this.scrollPreviewWidgetIntoView();
        this.onViewportChanged();
    }

    public componentDidUpdate(prevProps: VirtualizedGridProps): void {
        this.scrollPreviewWidgetIntoView();

        if (this.props.widgets !== prevProps.widgets) {
            this.onViewportChanged();
        }

        this._previousPreviewWidgetId = this.props.previewWidgetId;
    }

    public componentWillUnmount(): void {
        this.gridRef.current!.removeEventListener("scroll", this.onViewportChanged);
        window.removeEventListener("scroll", this.onViewportChanged);
        window.removeEventListener("resize", this.onViewportChanged);
    }

    public render(): JSX.Element | null {
        const gridMode = this.props.presentationMode === PresentationMode.Edit ? GridMode.Editable : GridMode.Readonly;
        const wrappingColumn = this.computeWrappingColumn();
        
        return (
            <div ref={this.gridRef} className="scroll-auto flex-grow flex-column">
                <DashboardGrid
                    mode={gridMode}
                    wrappingColumn={wrappingColumn}
                    onGridItemsMoved={this.props.onWidgetsMoved}
                    renderOnEmpty={this.props.renderOnEmpty}
                >
                    {this.props.widgets.map((w) => (
                        <GridItem
                            isFocalPreviewElement={this.props.previewWidgetId === w.id! && this.props.highlightOnPreview}
                            ref={(ref: HTMLDivElement | null) => this.updateGridItemRefElement(w.id!, ref)}
                            key={`grid-item-${w.id}`}
                            id={w.id!}
                            row={w.position.row}
                            column={w.position.column}
                            rowSpan={w.size.rowSpan}
                            columnSpan={w.size.columnSpan}
                            dragEvent={this.getDragEvent(w)}
                        >
                            {this.renderHost(w) && <WidgetHost {...this.createWidgetHostProps(w)} />}
                            <Observer deleteWidgetId={this._onDeleteWidgetId}>
                                {(props: { deleteWidgetId: string | undefined }) => {
                                    return props.deleteWidgetId && props.deleteWidgetId === w.id ? (
                                        this.renderDeleteWidgetDialog(w)
                                    ) : null;
                                }}
                            </Observer>
                        </GridItem>
                    ))}
                </DashboardGrid>
            </div>
        );
    }

    private renderDeleteWidgetDialog(widgetProps: DashboardWidgetProps): JSX.Element | null {
        const onDismiss = () => {
            this._onDeleteWidgetId.value = undefined;
        };

        const onDeleteWidgetConfirm = () => {
            this._onDeleteWidgetId.value = undefined;
            this.setPreviousWidget(widgetProps);
            this.props.onDeleteWidget(widgetProps);
        };

        return (
            <Dialog
                titleProps={{ text: Resources.DeleteWidgetDialog_Title }}
                footerButtonProps={[
                    {
                        text: Resources.DeleteWidgetDialog_ConfirmDeleteText,
                        onClick: onDeleteWidgetConfirm,
                        primary: true
                    },
                    {
                        text: Resources.DeleteWidgetDialog_CancelDeleteText,
                        onClick: onDismiss
                    }
                ]}
                onDismiss={onDismiss}
            >
                {Resources.DeleteWidgetDialog_Message}
            </Dialog>
        );
    }

    private getDragEvent(widget: Widget): Event | undefined {
        if (this.props.draggedWidgetId === widget.id && this.props.draggedEvent) {
            return this.props.draggedEvent;
        }
    }

    private scrollPreviewWidgetIntoView(): void {
        if (this.props.previewWidgetId) {
            const element = this.gridItemRefElements[this.props.previewWidgetId];
            if (element) {
                scrollElementIntoViewport(element, this.gridRef.current!, this.viewport);
            }
        }
    }

    private computeWrappingColumn(): number {
        let wrappingColumn = 1;
        if (this.gridRef.current != null) {
            const { left, right } = this.viewport;
            const visibleColumnCount = Math.ceil((right - left) / (WidgetSizeConverter.CellWidth + WidgetSizeConverter.CellMarginWidth));

            // It isn't clear when the viewport would produce a value of 0 for visibleColumnCount, but it has happened,
            // so we avoid the problem by making 1 the minimum wrapping column value.
            wrappingColumn = Math.max(1, visibleColumnCount);
        }

        // We enforce the max limit of the grid width here.
        return Math.min(wrappingColumn, VirtualizedGrid.MaximumGridColumns);
    }

    /** Returns props used by a WidgetHost to render the widget. */
    private createWidgetHostProps(widget: Widget): WidgetHostProps {
        return {
            widgetPresentationMode: mapWidgetHostMode(this.props.presentationMode),
            parentPageName: "Dashboard",
            widgetProps: widget,
            widgetHostParentProps: {
                onWidgetLoaded: this.props.onWidgetLoaded,
                onConfigureWidget: this.props.onConfigureWidget,
                onDeleteWidget: (widgetProps => {
                    this._onDeleteWidgetId.value = widgetProps.id;
                }),
                onCopyWidget: this.props.onCopyWidget,
                onWidgetMoved: this.props.onWidgetsMoved
            },
            userCanConfigure: this.props.canEditDashboard,
            openLinksInNewTab: this.props.widgetsShouldOpenLinksInNewTab,
            convertToPixelSize: WidgetSizeConverter.ConvertToPixelSize,
            teamContext: this.props.team,
            shouldFocusMoreActionsButtonByDefault: this.props.presentationMode === PresentationMode.View && this._previousPreviewWidgetId === widget.id!
        };
    }

    private updateGridItemRefElement(widgetId: string, ref: HTMLDivElement | null): void {
        this.gridItemRefElements[widgetId] = ref;
    }

    private renderHost(widgetData: Widget): boolean {
        return this.props.shouldRenderWidgetMap[widgetData.id!];
    }

    private setPreviousWidget(deletedWidget: Widget) {
        var prevWidget: Widget | null = null;

        // Find the previous widget on the dashboards.
        for (const widget of this.props.widgets) {
            if (this.isWidgetPositionedBefore(widget, deletedWidget)) {
                continue;
            }
            if (!prevWidget || this.isWidgetPositionedBefore(widget, prevWidget)) {
                prevWidget = widget;
            }
        }
        if (prevWidget) {
            this._previousPreviewWidgetId = prevWidget.id;
        }
    }


    private isWidgetPositionedBefore(widget1: Widget, widget2: Widget): boolean {
        if (widget2.position.row > widget1.position.row) {
            return false;
        }
        if (widget2.position.row < widget1.position.row) {
            return true;
        }
        if (widget2.position.column > widget1.position.column) {
            return false;
        }
        return true;
    }
}

const Actions = {
    onDeleteWidget: DashboardActions.deleteWidget,
    onCopyWidget: DashboardActions.copyWidget,
    onConfigureWidget: DashboardActions.configureWidget,
    onWidgetLoaded: DashboardActions.notifyWidgetLoaded,
    onViewportChanged: DashboardActions.updateWidgetsSeen,
    onWidgetsMoved: DashboardActions.notifyWidgetsMoved
};

function mapStateToProps(
    state: DashboardViewAwareState & ITTIAwareState,
    ownProps: VirtualizedGridOwnProps,
    pageContext: IVssPageContext
): VirtualizedGridMappedProps {
    return {
        widgets: Selectors.getActiveWidgets(state),
        canEditDashboard: Selectors.canEditDashboard(state),
        previewWidgetId: Selectors.getPreviewWidgetId(state),
        highlightOnPreview: Selectors.getPreviewHighlighting(state),
        shouldRenderWidgetMap: Selectors.getShouldRenderWidgetMap(state, pageContext),
        presentationMode: Selectors.getPresentationMode(state),
        widgetsShouldOpenLinksInNewTab: Selectors.getEmbedded(state),
        draggedWidgetId: Selectors.getDraggedWidgetId(state),
        draggedEvent: Selectors.getDraggedEvent(state),
        team: Selectors.getTeamDataOrDefault(state)
    };
}

export const ConnectedVirtualizedGrid = connectWithPageContext(mapStateToProps, Actions)(VirtualizedGrid);

function mapWidgetHostMode(dashboardMode: PresentationMode): WidgetPresentationMode {
    let widgetHostMode;
    switch (dashboardMode) {
        case PresentationMode.View:
        case PresentationMode.Copy:
            widgetHostMode = WidgetPresentationMode.dashboardView;
            break;
        case PresentationMode.Edit:
            widgetHostMode = WidgetPresentationMode.dashboardEditView;
            break;
        case PresentationMode.Config:
            widgetHostMode = WidgetPresentationMode.dashboardConfigPreview;
            break;
        default:
            throw new Error("Unexpected dashboard mode supplied");
    }
    return widgetHostMode;
}
